'use client';

import { MaterialReactTable, type MRT_ColumnDef, type MRT_SortingState } from 'material-react-table';
import {
  Autocomplete,
  Avatar,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import TitleBar from '../layout/TitleBar';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import HistoryIcon from '@mui/icons-material/History';
import MergeTypeIcon from '@mui/icons-material/MergeType';
import AddIcon from '@mui/icons-material/Add';
import { useNotification } from '@/contexts/NotificationContext';
import {
  getInventoryImageUploadUrl,
  getInventoryBalanceQuantity,
  getInventoryThumbnailUploadUrl,
  getInventoryWarehouseSite,
  importOffInventoryImages,
  lookupInventoryUpc,
  queryTomVendingInventory,
  mergeTomVendingInventoryItems,
  addTomVendingInventoryVariant,
  removeTomVendingInventoryVariant,
  queryTomVendingInventoryMonetaryHistory,
  upsertInventoryBalance,
  uploadToPresignedUrl,
  useCreateTomVendingInventoryItem,
  useDeleteTomVendingInventoryItem,
  useTomVendingBrandsList,
  useTomVendingCategoriesList,
  useUpdateTomVendingInventoryItem,
  type MonetaryHistoryRow,
  type TomVendingInventoryRow,
  type TomVendingVariantUpc,
} from '@/hooks/useTomVendingInventory';
import { bulkUpsertTomVendingSalePrices } from '@/hooks/useTomVendingReports';
import { useTomVendingVendorsList, type TvVendor } from '@/hooks/useTomVendingPurchasing';

const DEFAULT_PAGE_SIZE = 25;

function formatCents(cents: number | null | undefined): string {
  if (cents == null) return '—';
  return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' }).format(cents / 100);
}

function centsToInputStr(cents: number | null | undefined): string {
  if (cents == null) return '';
  return (cents / 100).toFixed(2);
}

function suggestDisplayNameFromMerch(brand: string, flavor: string, weightText: string): string {
  const parts = [brand.trim(), flavor.trim()].filter(Boolean);
  let s = parts.join(' ');
  const w = weightText.trim();
  if (w) {
    s = s ? `${s} (${w})` : w;
  }
  return s;
}

function parseMoneyInputToCents(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseFloat(t);
  if (Number.isNaN(n) || n < 0) {
    throw new Error('INVALID_MONEY');
  }
  return Math.round(n * 100);
}

function extFromFile(f: File): string {
  const n = f.name.split('.').pop();
  return (n || 'jpg').toLowerCase();
}

function revokePreviewUrl(url: string | null) {
  if (url?.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
}

const TomsVendingInventoryPage: React.FC = () => {
  const [data, setData] = useState<TomVendingInventoryRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: DEFAULT_PAGE_SIZE });
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: 'id', desc: true }]);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tableRefreshKey, setTableRefreshKey] = useState(0);

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [priceImportOpen, setPriceImportOpen] = useState(false);
  const [priceImportText, setPriceImportText] = useState('');
  const [priceImportLoading, setPriceImportLoading] = useState(false);
  const [priceImportResult, setPriceImportResult] = useState<string | null>(null);
  const [editing, setEditing] = useState<TomVendingInventoryRow | null>(null);
  const [deleting, setDeleting] = useState<TomVendingInventoryRow | null>(null);

  const [formUpc, setFormUpc] = useState('');
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formImageKey, setFormImageKey] = useState<string | null>(null);
  const [formThumbKey, setFormThumbKey] = useState<string | null>(null);
  const [canonicalFromOff, setCanonicalFromOff] = useState(true);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);
  const [thumbPreviewUrl, setThumbPreviewUrl] = useState<string | null>(null);
  const imageBlobRef = useRef<string | null>(null);
  const thumbBlobRef = useRef<string | null>(null);
  const [offLookupLoading, setOffLookupLoading] = useState(false);
  const offSyncInFlightRef = useRef(false);
  /** Dedup auto-sync when canonical UPC from OFF settles to the same digits after normalization. */
  const lastSuccessfulOffLookupRef = useRef<string | null>(null);
  /** Latest image state for OFF import error rollback without stale useCallback closures. */
  const formImageKeyRef = useRef<string | null>(null);
  const formThumbKeyRef = useRef<string | null>(null);
  const imagePreviewUrlRef = useRef<string | null>(null);
  const thumbPreviewUrlRef = useRef<string | null>(null);
  formImageKeyRef.current = formImageKey;
  formThumbKeyRef.current = formThumbKey;
  imagePreviewUrlRef.current = imagePreviewUrl;
  thumbPreviewUrlRef.current = thumbPreviewUrl;

  const [uploadBusy, setUploadBusy] = useState<'image' | 'thumb' | null>(null);

  const [warehouseSiteId, setWarehouseSiteId] = useState<number | null>(null);
  const [warehouseSiteLoading, setWarehouseSiteLoading] = useState(true);
  const [warehouseDialogOpen, setWarehouseDialogOpen] = useState(false);
  const [warehouseRow, setWarehouseRow] = useState<TomVendingInventoryRow | null>(null);
  const [warehouseQty, setWarehouseQty] = useState('0');
  const [warehouseBusyItemId, setWarehouseBusyItemId] = useState<number | null>(null);
  const [warehouseSaving, setWarehouseSaving] = useState(false);

  const [formPriceStr, setFormPriceStr] = useState('');
  const [formCostStr, setFormCostStr] = useState('');
  const [formMonetaryNote, setFormMonetaryNote] = useState('');
  const [formBrandId, setFormBrandId] = useState<number | null>(null);
  const [formFlavor, setFormFlavor] = useState('');
  const [formWeightText, setFormWeightText] = useState('');
  const [formCategoryId, setFormCategoryId] = useState<number | null>(null);
  const [formSchoolApproved, setFormSchoolApproved] = useState(false);
  const [formInternalNotes, setFormInternalNotes] = useState('');
  const [formPreferredVendor, setFormPreferredVendor] = useState<TvVendor | null>(null);
  const [formAltId1, setFormAltId1] = useState('');
  const [formAltId2, setFormAltId2] = useState('');
  const [formAltId3, setFormAltId3] = useState('');
  const [formAltId4, setFormAltId4] = useState('');

  const { data: vendorOptions = [] } = useTomVendingVendorsList();
  const { data: brandOptions = [] } = useTomVendingBrandsList();
  const { data: categoryOptions = [] } = useTomVendingCategoriesList();

  const [editVariantRows, setEditVariantRows] = useState<TomVendingVariantUpc[]>([]);
  const [newVariantUpc, setNewVariantUpc] = useState('');
  const [newVariantLabel, setNewVariantLabel] = useState('');
  const [variantBusy, setVariantBusy] = useState(false);

  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeTarget, setMergeTarget] = useState<TomVendingInventoryRow | null>(null);
  const [mergeSearch, setMergeSearch] = useState('');
  const [mergeOptions, setMergeOptions] = useState<TomVendingInventoryRow[]>([]);
  const [mergeLoading, setMergeLoading] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyFor, setHistoryFor] = useState<TomVendingInventoryRow | null>(null);
  const [historyRows, setHistoryRows] = useState<MonetaryHistoryRow[]>([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(false);

  const createMutation = useCreateTomVendingInventoryItem();
  const updateMutation = useUpdateTomVendingInventoryItem();
  const deleteMutation = useDeleteTomVendingInventoryItem();
  const { showSuccess, showError } = useNotification();

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => window.clearTimeout(t);
  }, [search]);

  useEffect(() => {
    void (async () => {
      try {
        const s = await getInventoryWarehouseSite();
        setWarehouseSiteId(s?.id ?? null);
      } catch {
        setWarehouseSiteId(null);
      } finally {
        setWarehouseSiteLoading(false);
      }
    })();
  }, []);

  const openWarehouseStock = useCallback(
    async (row: TomVendingInventoryRow) => {
      if (warehouseSiteId == null) {
        showError('Warehouse is not configured. Apply the latest database migration.');
        return;
      }
      setWarehouseBusyItemId(row.id);
      try {
        const q = await getInventoryBalanceQuantity(row.id, warehouseSiteId);
        setWarehouseQty(String(q));
        setWarehouseRow(row);
        setWarehouseDialogOpen(true);
      } catch (e: any) {
        showError(e?.message || 'Failed to load warehouse quantity');
      } finally {
        setWarehouseBusyItemId(null);
      }
    },
    [warehouseSiteId, showError],
  );

  const saveWarehouseStock = useCallback(async () => {
    if (!warehouseRow || warehouseSiteId == null) return;
    const n = parseInt(warehouseQty, 10);
    if (Number.isNaN(n) || n < 0) {
      showError('Enter a valid quantity');
      return;
    }
    setWarehouseSaving(true);
    try {
      await upsertInventoryBalance({
        inventoryItemId: warehouseRow.id,
        storageSiteId: warehouseSiteId,
        quantity: n,
      });
      showSuccess('Warehouse stock updated');
      setWarehouseDialogOpen(false);
      setWarehouseRow(null);
    } catch (e: any) {
      showError(e?.message || 'Save failed');
    } finally {
      setWarehouseSaving(false);
    }
  }, [warehouseRow, warehouseSiteId, warehouseQty, showError, showSuccess]);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const sortCol = sorting[0]?.id || 'id';
      const result = await queryTomVendingInventory({
        skip: pagination.pageIndex * pagination.pageSize,
        take: pagination.pageSize,
        search: debouncedSearch,
        orderBy: sortCol,
        orderDirection: sorting[0]?.desc ? 'DESC' : 'ASC',
      });
      setData(result.data);
      setTotalRows(result.total);
    } catch (e: any) {
      showError(e?.message || 'Failed to load inventory');
      setData([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }, [pagination, sorting, debouncedSearch, tableRefreshKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const resetForm = () => {
    lastSuccessfulOffLookupRef.current = null;
    setFormUpc('');
    setFormName('');
    setFormDescription('');
    setFormImageKey(null);
    setFormThumbKey(null);
    setCanonicalFromOff(true);
    revokePreviewUrl(imageBlobRef.current);
    revokePreviewUrl(thumbBlobRef.current);
    imageBlobRef.current = null;
    thumbBlobRef.current = null;
    setImagePreviewUrl(null);
    setThumbPreviewUrl(null);
    setFormPriceStr('');
    setFormCostStr('');
    setFormMonetaryNote('');
    setFormBrandId(null);
    setFormFlavor('');
    setFormWeightText('');
    setFormCategoryId(null);
    setFormSchoolApproved(false);
    setFormInternalNotes('');
    setFormPreferredVendor(null);
    setFormAltId1('');
    setFormAltId2('');
    setFormAltId3('');
    setFormAltId4('');
    setEditVariantRows([]);
    setNewVariantUpc('');
    setNewVariantLabel('');
  };

  const openCreate = () => {
    resetForm();
    setCreateOpen(true);
  };

  const openEdit = (row: TomVendingInventoryRow) => {
    lastSuccessfulOffLookupRef.current = null;
    revokePreviewUrl(imageBlobRef.current);
    revokePreviewUrl(thumbBlobRef.current);
    imageBlobRef.current = null;
    thumbBlobRef.current = null;
    setEditing(row);
    setFormUpc(row.upc);
    setFormName(row.name || '');
    setFormDescription(row.description || '');
    setFormImageKey(row.imageS3Key);
    setFormThumbKey(row.thumbnailS3Key);
    setCanonicalFromOff(false);
    setImagePreviewUrl(row.imageUrl ?? null);
    setThumbPreviewUrl(row.thumbnailUrl ?? row.imageUrl ?? null);
    setFormPriceStr(centsToInputStr(row.priceCents));
    setFormCostStr(centsToInputStr(row.costCents));
    setFormMonetaryNote('');
    setFormBrandId(row.brandId ?? null);
    setFormFlavor(row.flavor ?? '');
    setFormWeightText(row.weightText ?? '');
    setFormCategoryId(row.categoryId ?? null);
    setFormSchoolApproved(row.schoolApproved ?? false);
    setFormInternalNotes(row.internalNotes ?? '');
    setFormPreferredVendor(
      row.preferredVendorId != null
        ? vendorOptions.find((v) => v.id === row.preferredVendorId) ??
            (row.preferredVendorName
              ? { id: row.preferredVendorId, name: row.preferredVendorName }
              : null)
        : null,
    );
    setFormAltId1(row.altIdentifier1 ?? '');
    setFormAltId2(row.altIdentifier2 ?? '');
    setFormAltId3(row.altIdentifier3 ?? '');
    setFormAltId4(row.altIdentifier4 ?? '');
    setEditVariantRows(row.variantUpcs ? [...row.variantUpcs] : []);
    setEditOpen(true);
  };

  const performOpenFoodFactsSync = useCallback(async (
    rawUpc: string,
    opts?: { allowRepeatLookup?: boolean; silent?: boolean; autoLookup?: boolean },
  ) => {
    const u = rawUpc.trim();
    if (!u) return;
    const normalizedKey = u.replace(/\D/g, '');
    if (!opts?.allowRepeatLookup && lastSuccessfulOffLookupRef.current === normalizedKey) return;
    if (offSyncInFlightRef.current) return;
    offSyncInFlightRef.current = true;
    setOffLookupLoading(true);
    try {
      const res = await lookupInventoryUpc(u);
      if (!res.found) {
        if (!opts?.autoLookup) {
          showError('No product found in Open Food Facts for this UPC');
          setCanonicalFromOff(false);
        }
        lastSuccessfulOffLookupRef.current = null;
        return;
      }
      setCanonicalFromOff(true);
      if (res.code) {
        setFormUpc(res.code);
      }
      if (res.name) {
        setFormName(res.name);
      }
      if (res.description != null) {
        setFormDescription(res.description);
      }

      const hasOffImage = Boolean(res.imageUrl?.trim());
      const hasOffThumb = Boolean(res.imageThumbUrl?.trim());

      const prevImageKey = formImageKeyRef.current;
      const prevThumbKey = formThumbKeyRef.current;
      const prevImagePreview = imagePreviewUrlRef.current;
      const prevThumbPreview = thumbPreviewUrlRef.current;

      if (hasOffImage || hasOffThumb) {
        const mainPreview = hasOffImage ? res.imageUrl! : res.imageThumbUrl!;
        const thumbPreview = hasOffThumb ? res.imageThumbUrl! : mainPreview;
        setImagePreviewUrl(mainPreview);
        setThumbPreviewUrl(thumbPreview);
        try {
          const out = await importOffInventoryImages({
            imageUrl: hasOffImage ? res.imageUrl! : undefined,
            imageThumbUrl: hasOffThumb ? res.imageThumbUrl! : undefined,
          });
          setFormImageKey(out.imageS3Key ?? null);
          setFormThumbKey(out.thumbnailS3Key ?? null);
          revokePreviewUrl(imageBlobRef.current);
          revokePreviewUrl(thumbBlobRef.current);
          imageBlobRef.current = null;
          thumbBlobRef.current = null;
        } catch (importErr: any) {
          showError(importErr?.message || 'Could not copy images from Open Food Facts');
          setFormImageKey(prevImageKey);
          setFormThumbKey(prevThumbKey);
          setImagePreviewUrl(prevImagePreview);
          setThumbPreviewUrl(prevThumbPreview);
          lastSuccessfulOffLookupRef.current = null;
          return;
        }
      }

      lastSuccessfulOffLookupRef.current = (res.code ?? u).replace(/\D/g, '');
      if (!opts?.silent) {
        showSuccess('Synced from Open Food Facts');
      }
    } catch (e: any) {
      if (!opts?.silent) {
        showError(e?.message || 'Lookup failed');
      }
    } finally {
      offSyncInFlightRef.current = false;
      setOffLookupLoading(false);
    }
  }, [showError, showSuccess]);

  useEffect(() => {
    if (!createOpen && !editOpen) return;
    if (!canonicalFromOff) return;
    const digits = formUpc.replace(/\D/g, '');
    if (digits.length < 8) return;
    const u = formUpc.trim();
    if (!u) return;
    const t = window.setTimeout(() => {
      void performOpenFoodFactsSync(u, { silent: true, autoLookup: true });
    }, 450);
    return () => window.clearTimeout(t);
  }, [formUpc, canonicalFromOff, createOpen, editOpen, performOpenFoodFactsSync]);

  const handleFetchOff = async () => {
    const u = formUpc.trim();
    if (!u) {
      showError('Enter a UPC first');
      return;
    }
    await performOpenFoodFactsSync(u, { allowRepeatLookup: true });
  };

  const handleImageFile = async (file: File | null, kind: 'image' | 'thumb') => {
    if (!file) return;
    setUploadBusy(kind);
    const ext = extFromFile(file);
    const ct = file.type || 'image/jpeg';
    let pendingBlob: string | null = null;
    try {
      if (kind === 'image') {
        revokePreviewUrl(imageBlobRef.current);
        pendingBlob = URL.createObjectURL(file);
        imageBlobRef.current = pendingBlob;
        setImagePreviewUrl(pendingBlob);
        const { uploadUrl, s3Key } = await getInventoryImageUploadUrl(ext, ct);
        await uploadToPresignedUrl(uploadUrl, file, ct);
        setFormImageKey(s3Key);
      } else {
        revokePreviewUrl(thumbBlobRef.current);
        pendingBlob = URL.createObjectURL(file);
        thumbBlobRef.current = pendingBlob;
        setThumbPreviewUrl(pendingBlob);
        const { uploadUrl, s3Key } = await getInventoryThumbnailUploadUrl(ext, ct);
        await uploadToPresignedUrl(uploadUrl, file, ct);
        setFormThumbKey(s3Key);
      }
      setCanonicalFromOff(false);
      showSuccess(kind === 'image' ? 'Image uploaded' : 'Thumbnail uploaded');
    } catch (e: any) {
      if (kind === 'image') {
        if (pendingBlob) {
          URL.revokeObjectURL(pendingBlob);
          imageBlobRef.current = null;
        }
        setImagePreviewUrl(null);
      } else {
        if (pendingBlob) {
          URL.revokeObjectURL(pendingBlob);
          thumbBlobRef.current = null;
        }
        setThumbPreviewUrl(null);
      }
      showError(e?.message || 'Upload failed');
    } finally {
      setUploadBusy(null);
    }
  };

  const submitCreate = () => {
    if (!formUpc.trim()) {
      showError('UPC is required');
      return;
    }
    let priceCents: number | null;
    let costCents: number | null;
    try {
      priceCents = parseMoneyInputToCents(formPriceStr);
      costCents = parseMoneyInputToCents(formCostStr);
    } catch {
      showError('Enter valid non-negative dollar amounts for price and cost, or leave them blank');
      return;
    }
    createMutation.mutate(
      {
        upc: formUpc.trim(),
        name: formName.trim() || null,
        description: formDescription.trim() || null,
        imageS3Key: formImageKey,
        thumbnailS3Key: formThumbKey,
        canonicalUpcFromOff: canonicalFromOff,
        priceCents,
        costCents,
        brandId: formBrandId,
        flavor: formFlavor.trim() || null,
        weightText: formWeightText.trim() || null,
        categoryId: formCategoryId,
        schoolApproved: formSchoolApproved,
        internalNotes: formInternalNotes.trim() || null,
        preferredVendorId: formPreferredVendor?.id ?? null,
        altIdentifier1: formAltId1.trim() || null,
        altIdentifier2: formAltId2.trim() || null,
        altIdentifier3: formAltId3.trim() || null,
        altIdentifier4: formAltId4.trim() || null,
      },
      {
        onSuccess: () => {
          showSuccess('Inventory item created');
          setCreateOpen(false);
          resetForm();
          setTableRefreshKey((k) => k + 1);
        },
        onError: (e: any) => showError(e?.message || 'Create failed'),
      },
    );
  };

  const submitEdit = () => {
    if (!editing) return;
    if (!formUpc.trim()) {
      showError('UPC is required');
      return;
    }
    let priceCents: number | null;
    let costCents: number | null;
    try {
      priceCents = parseMoneyInputToCents(formPriceStr);
      costCents = parseMoneyInputToCents(formCostStr);
    } catch {
      showError('Enter valid non-negative dollar amounts for price and cost, or leave them blank');
      return;
    }
    const note = formMonetaryNote.trim();
    updateMutation.mutate(
      {
        id: editing.id,
        upc: formUpc.trim(),
        name: formName.trim() || null,
        description: formDescription.trim() || null,
        imageS3Key: formImageKey,
        thumbnailS3Key: formThumbKey,
        canonicalUpcFromOff: canonicalFromOff,
        priceCents,
        costCents,
        brandId: formBrandId,
        flavor: formFlavor.trim() || null,
        weightText: formWeightText.trim() || null,
        categoryId: formCategoryId,
        schoolApproved: formSchoolApproved,
        internalNotes: formInternalNotes.trim() || null,
        preferredVendorId: formPreferredVendor?.id ?? null,
        altIdentifier1: formAltId1.trim() || null,
        altIdentifier2: formAltId2.trim() || null,
        altIdentifier3: formAltId3.trim() || null,
        altIdentifier4: formAltId4.trim() || null,
        ...(note ? { monetaryChangeNote: note } : {}),
      },
      {
        onSuccess: () => {
          showSuccess('Inventory item updated');
          setEditOpen(false);
          setEditing(null);
          resetForm();
          setTableRefreshKey((k) => k + 1);
        },
        onError: (e: any) => showError(e?.message || 'Update failed'),
      },
    );
  };

  const confirmDelete = () => {
    if (!deleting) return;
    deleteMutation.mutate(
      { id: deleting.id },
      {
        onSuccess: () => {
          showSuccess('Deleted');
          setDeleteOpen(false);
          setDeleting(null);
          setTableRefreshKey((k) => k + 1);
        },
        onError: (e: any) => showError(e?.message || 'Delete failed'),
      },
    );
  };

  useEffect(() => {
    if (!mergeOpen) {
      setMergeTarget(null);
      setMergeSearch('');
      setMergeOptions([]);
      return;
    }
    let cancelled = false;
    setMergeLoading(true);
    const t = window.setTimeout(() => {
      void queryTomVendingInventory({
        skip: 0,
        take: 40,
        search: mergeSearch.trim(),
        orderBy: 'upc',
        orderDirection: 'ASC',
      })
        .then((res) => {
          if (cancelled) return;
          const curId = editing?.id;
          setMergeOptions(curId != null ? res.data.filter((r) => r.id !== curId) : res.data);
        })
        .finally(() => {
          if (!cancelled) setMergeLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      window.clearTimeout(t);
    };
  }, [mergeOpen, mergeSearch, editing?.id]);

  useEffect(() => {
    if (!historyOpen || !historyFor) {
      setHistoryRows([]);
      setHistoryTotal(0);
      return;
    }
    let cancelled = false;
    setHistoryLoading(true);
    void queryTomVendingInventoryMonetaryHistory({ inventoryItemId: historyFor.id, skip: 0, take: 100 })
      .then((r) => {
        if (!cancelled) {
          setHistoryRows(r.data);
          setHistoryTotal(r.total);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setHistoryRows([]);
          setHistoryTotal(0);
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [historyOpen, historyFor?.id]);

  const handleAddVariant = async () => {
    if (!editing) return;
    const u = newVariantUpc.trim();
    if (!u) {
      showError('Enter a variant UPC');
      return;
    }
    setVariantBusy(true);
    try {
      const v = await addTomVendingInventoryVariant({
        inventoryItemId: editing.id,
        upc: u,
        label: newVariantLabel.trim() || null,
      });
      setEditVariantRows((rows) => [...rows, { id: v.id, upc: v.upc, label: v.label }]);
      setNewVariantUpc('');
      setNewVariantLabel('');
      setTableRefreshKey((k) => k + 1);
      showSuccess('Variant UPC added');
    } catch (e: any) {
      showError(e?.message || 'Could not add variant');
    } finally {
      setVariantBusy(false);
    }
  };

  const handleRemoveVariant = async (vid: number) => {
    setVariantBusy(true);
    try {
      await removeTomVendingInventoryVariant(vid);
      setEditVariantRows((rows) => rows.filter((r) => r.id !== vid));
      setTableRefreshKey((k) => k + 1);
      showSuccess('Variant removed');
    } catch (e: any) {
      showError(e?.message || 'Remove failed');
    } finally {
      setVariantBusy(false);
    }
  };

  const confirmMerge = async () => {
    if (!editing || !mergeTarget) return;
    setMergeSaving(true);
    try {
      await mergeTomVendingInventoryItems({
        keepInventoryItemId: editing.id,
        mergeInventoryItemId: mergeTarget.id,
      });
      showSuccess('Catalog lines merged. Stock and history are combined into this line.');
      setMergeOpen(false);
      setEditOpen(false);
      setEditing(null);
      resetForm();
      setTableRefreshKey((k) => k + 1);
    } catch (e: any) {
      showError(e?.message || 'Merge failed');
    } finally {
      setMergeSaving(false);
    }
  };

  const columns = useMemo<MRT_ColumnDef<TomVendingInventoryRow>[]>(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 132,
        enableSorting: false,
        Cell: ({ row }) => (
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => void openWarehouseStock(row.original)}
              disabled={warehouseSiteLoading || warehouseBusyItemId === row.original.id}
              aria-label="Warehouse stock"
            >
              <WarehouseIcon fontSize="small" />
            </IconButton>
            <IconButton size="small" onClick={() => openEdit(row.original)} aria-label="Edit">
              <EditIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => {
                setHistoryFor(row.original);
                setHistoryOpen(true);
              }}
              aria-label="Price and cost history"
            >
              <HistoryIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              color="error"
              onClick={() => {
                setDeleting(row.original);
                setDeleteOpen(true);
              }}
              aria-label="Delete"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      { accessorKey: 'id', header: 'ID', size: 72 },
      {
        id: 'thumbnail',
        header: '',
        size: 56,
        enableSorting: false,
        Cell: ({ row }) => {
          const src = row.original.thumbnailUrl || row.original.imageUrl;
          return src ? (
            <Avatar src={src} variant="rounded" sx={{ width: 40, height: 40 }} />
          ) : (
            <Avatar variant="rounded" sx={{ width: 40, height: 40 }}>
              <Inventory2Icon fontSize="small" />
            </Avatar>
          );
        },
      },
      { accessorKey: 'upc', header: 'UPC' },
      {
        accessorKey: 'name',
        header: 'Name',
        Cell: ({ cell }) => (cell.getValue() as string) || '—',
      },
      {
        accessorKey: 'brandName',
        header: 'Brand',
        enableHiding: true,
        Cell: ({ row }) => row.original.brandName || '—',
      },
      {
        accessorKey: 'categoryName',
        header: 'Category',
        enableHiding: true,
        Cell: ({ row }) => row.original.categoryName || '—',
      },
      {
        accessorKey: 'schoolApproved',
        header: 'School',
        enableHiding: true,
        size: 72,
        Cell: ({ row }) => (row.original.schoolApproved ? 'Yes' : '—'),
      },
      {
        accessorKey: 'priceCents',
        header: 'Price',
        size: 100,
        Cell: ({ row }) => formatCents(row.original.priceCents),
      },
      {
        accessorKey: 'costCents',
        header: 'Cost',
        size: 100,
        Cell: ({ row }) => formatCents(row.original.costCents),
      },
      {
        id: 'variantUpcs',
        header: 'Alt UPCs',
        size: 84,
        enableSorting: false,
        Cell: ({ row }) => {
          const n = row.original.variantUpcs?.length ?? 0;
          return n > 0 ? String(n) : '—';
        },
      },
      {
        id: 'altIdentifiers',
        header: 'Alt IDs',
        size: 72,
        enableSorting: false,
        Cell: ({ row }) => {
          const vals = [
            row.original.altIdentifier1,
            row.original.altIdentifier2,
            row.original.altIdentifier3,
            row.original.altIdentifier4,
          ].filter((v) => v != null && String(v).trim() !== '');
          return vals.length > 0 ? String(vals.length) : '—';
        },
      },
      {
        accessorKey: 'description',
        header: 'Description',
        Cell: ({ cell }) => {
          const v = (cell.getValue() as string) || '';
          if (!v) return '—';
          const t = v.length > 80 ? `${v.slice(0, 80)}…` : v;
          return t;
        },
      },
      {
        accessorKey: 'createdAt',
        header: 'Created',
        enableHiding: true,
        Cell: ({ row }) =>
          row.original.createdAt ? new Date(row.original.createdAt).toLocaleDateString() : '—',
      },
    ],
    [openWarehouseStock, warehouseSiteLoading, warehouseBusyItemId],
  );

  const breadcrumbs = [
    { label: 'Home', href: '/app/dashboard' },
    { label: 'Catalog' },
  ];

  const formDialog = (isEdit: boolean) => (
    <Dialog
      open={isEdit ? editOpen : createOpen}
      onClose={() => {
        if (isEdit) {
          setEditOpen(false);
          setEditing(null);
        } else {
          setCreateOpen(false);
        }
        resetForm();
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>{isEdit ? formUpc.trim() || editing?.upc || 'Edit item' : 'Add inventory item'}</DialogTitle>
      <DialogContent>
        <Box sx={{ width: '100%', pt: 0.5 }}>
          <Box sx={{ display: 'flex', gap: 2, mb: 3, alignItems: 'flex-start' }}>
            <Avatar
              variant="rounded"
              src={(thumbPreviewUrl || imagePreviewUrl) || undefined}
              sx={{ width: 120, height: 120, flexShrink: 0 }}
            >
              {offLookupLoading && !(thumbPreviewUrl || imagePreviewUrl) ? (
                <CircularProgress size={40} />
              ) : (
                <Inventory2Icon sx={{ fontSize: 48 }} />
              )}
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="Name"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                size="small"
                fullWidth
                InputProps={{ sx: { fontSize: '1.25rem', fontWeight: 600 } }}
              />
              <TextField
                label="UPC"
                value={formUpc}
                onChange={(e) => {
                  lastSuccessfulOffLookupRef.current = null;
                  setFormUpc(e.target.value);
                }}
                size="small"
                fullWidth
                required
              />
            </Box>
          </Box>

          <FormControlLabel
            control={
              <Checkbox
                checked={canonicalFromOff}
                onChange={(_, checked) => {
                  setCanonicalFromOff(checked);
                  if (!checked) {
                    lastSuccessfulOffLookupRef.current = null;
                  }
                }}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">Canonical UPC from Open Food Facts</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  On by default — after enough digits (~8+) are entered, product data loads from OFF without tapping Fetch,
                  including images when available. Uncheck if you prefer manual lookups only.
                </Typography>
              </Box>
            }
            sx={{ alignItems: 'flex-start', mb: 1 }}
          />
          <Button
            variant="contained"
            size="medium"
            onClick={handleFetchOff}
            disabled={offLookupLoading || !formUpc.trim()}
            sx={{ textTransform: 'none', mb: 2, display: 'block' }}
          >
            {offLookupLoading ? 'Syncing…' : 'Fetch from Open Food Facts'}
          </Button>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Merchandising (stock list)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 1 }}>
            <Autocomplete
              size="small"
              options={brandOptions}
              value={brandOptions.find((b) => b.id === formBrandId) ?? null}
              onChange={(_, v) => setFormBrandId(v?.id ?? null)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Brand" />}
              sx={{ minWidth: 140, flex: 1 }}
            />
            <TextField
              label="Flavor"
              value={formFlavor}
              onChange={(e) => setFormFlavor(e.target.value)}
              size="small"
              sx={{ minWidth: 120, flex: 1 }}
            />
            <TextField
              label="Weight"
              value={formWeightText}
              onChange={(e) => setFormWeightText(e.target.value)}
              size="small"
              placeholder="e.g. 1.75oz"
              sx={{ minWidth: 100 }}
            />
            <Autocomplete
              size="small"
              options={categoryOptions}
              value={categoryOptions.find((c) => c.id === formCategoryId) ?? null}
              onChange={(_, v) => setFormCategoryId(v?.id ?? null)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Category" />}
              sx={{ minWidth: 140, flex: 1 }}
            />
          </Box>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formSchoolApproved}
                  onChange={(e) => setFormSchoolApproved(e.target.checked)}
                  size="small"
                />
              }
              label="School approved"
            />
            <Autocomplete
              size="small"
              options={vendorOptions}
              value={formPreferredVendor}
              onChange={(_, v) => setFormPreferredVendor(v)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              renderInput={(params) => <TextField {...params} label="Preferred vendor" />}
              sx={{ minWidth: 200, flex: 1 }}
            />
            <Button
              size="small"
              variant="text"
              sx={{ textTransform: 'none' }}
              onClick={() => {
                const brandName = brandOptions.find((b) => b.id === formBrandId)?.name ?? '';
                const suggested = suggestDisplayNameFromMerch(brandName, formFlavor, formWeightText);
                if (suggested) setFormName(suggested);
              }}
            >
              Suggest name from brand / flavor
            </Button>
          </Box>
          <TextField
            label="Internal notes"
            value={formInternalNotes}
            onChange={(e) => setFormInternalNotes(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
            placeholder="Ops notes (not the Open Food Facts description)"
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
            Alt identifiers (manufacturer / vendor item #)
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Free-form lookup keys used when matching purchase orders (not barcodes — use Alt UPCs for those).
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Alt identifier 1"
              value={formAltId1}
              onChange={(e) => setFormAltId1(e.target.value)}
              size="small"
              sx={{ minWidth: 140, flex: 1 }}
            />
            <TextField
              label="Alt identifier 2"
              value={formAltId2}
              onChange={(e) => setFormAltId2(e.target.value)}
              size="small"
              sx={{ minWidth: 140, flex: 1 }}
            />
            <TextField
              label="Alt identifier 3"
              value={formAltId3}
              onChange={(e) => setFormAltId3(e.target.value)}
              size="small"
              sx={{ minWidth: 140, flex: 1 }}
            />
            <TextField
              label="Alt identifier 4"
              value={formAltId4}
              onChange={(e) => setFormAltId4(e.target.value)}
              size="small"
              sx={{ minWidth: 140, flex: 1 }}
            />
          </Box>

          <TextField
            label="Description"
            value={formDescription}
            onChange={(e) => setFormDescription(e.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={3}
            placeholder="No description stored."
            sx={{ mb: 2 }}
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Upload or replace stored images (optional). Preview updates when Open Food Facts returns artwork.
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'flex-start', mb: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
              <Typography variant="caption" color="text.secondary">
                Main image
              </Typography>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {offLookupLoading && !imagePreviewUrl ? (
                  <CircularProgress size={28} />
                ) : imagePreviewUrl ? (
                  <Box
                    component="img"
                    src={imagePreviewUrl}
                    alt=""
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.disabled" sx={{ px: 0.5, textAlign: 'center' }}>
                    None
                  </Typography>
                )}
              </Box>
              <Button variant="outlined" component="label" size="small" disabled={uploadBusy !== null} sx={{ textTransform: 'none' }}>
                {uploadBusy === 'image' ? 'Uploading…' : 'Upload'}
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    void handleImageFile(f || null, 'image');
                  }}
                />
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-start' }}>
              <Typography variant="caption" color="text.secondary">
                Thumbnail
              </Typography>
              <Box
                sx={{
                  width: 100,
                  height: 100,
                  borderRadius: 1,
                  border: 1,
                  borderColor: 'divider',
                  bgcolor: 'action.hover',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {offLookupLoading && !thumbPreviewUrl ? (
                  <CircularProgress size={24} />
                ) : thumbPreviewUrl ? (
                  <Box
                    component="img"
                    src={thumbPreviewUrl}
                    alt=""
                    sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                  />
                ) : (
                  <Typography variant="caption" color="text.disabled" sx={{ px: 0.5, textAlign: 'center' }}>
                    None
                  </Typography>
                )}
              </Box>
              <Button variant="outlined" component="label" size="small" disabled={uploadBusy !== null} sx={{ textTransform: 'none' }}>
                {uploadBusy === 'thumb' ? 'Uploading…' : 'Upload'}
                <input
                  hidden
                  accept="image/*"
                  type="file"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    e.target.value = '';
                    void handleImageFile(f || null, 'thumb');
                  }}
                />
              </Button>
            </Box>
          </Box>

          <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
            Price and cost (USD)
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <TextField
              label="Retail price"
              value={formPriceStr}
              onChange={(e) => setFormPriceStr(e.target.value)}
              size="small"
              placeholder="e.g. 1.25"
              sx={{ minWidth: 140 }}
              helperText="Blank if unknown"
            />
            <TextField
              label="Unit cost"
              value={formCostStr}
              onChange={(e) => setFormCostStr(e.target.value)}
              size="small"
              placeholder="e.g. 0.65"
              sx={{ minWidth: 140 }}
              helperText="Blank if unknown"
            />
          </Box>
          {isEdit && (
            <TextField
              label="Note on price or cost change (optional)"
              value={formMonetaryNote}
              onChange={(e) => setFormMonetaryNote(e.target.value)}
              size="small"
              fullWidth
              placeholder="e.g. supplier increase, new vendor"
              sx={{ mb: 2 }}
            />
          )}
          {isEdit && (
            <Box sx={{ borderTop: 1, borderColor: 'divider', pt: 2, mt: 1 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Alternate UPC codes (variants)
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Same slot item with different packaging (seasonal barcode, etc.). Search and machine picks match any of
                these codes to this line.
              </Typography>
              <List dense disablePadding sx={{ mb: 1 }}>
                {editVariantRows.length === 0 ? (
                  <Typography variant="body2" color="text.disabled">
                    No alternate UPCs yet
                  </Typography>
                ) : (
                  editVariantRows.map((v) => (
                    <ListItem key={v.id} disableGutters>
                      <ListItemText primary={v.upc} secondary={v.label || undefined} />
                      <ListItemSecondaryAction>
                        <IconButton
                          edge="end"
                          size="small"
                          aria-label="Remove variant"
                          disabled={variantBusy}
                          onClick={() => void handleRemoveVariant(v.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))
                )}
              </List>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                <TextField
                  label="Variant UPC"
                  value={newVariantUpc}
                  onChange={(e) => setNewVariantUpc(e.target.value)}
                  size="small"
                  sx={{ minWidth: 160 }}
                />
                <TextField
                  label="Label (optional)"
                  value={newVariantLabel}
                  onChange={(e) => setNewVariantLabel(e.target.value)}
                  size="small"
                  sx={{ minWidth: 160 }}
                  placeholder="e.g. Holiday pack"
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => void handleAddVariant()}
                  disabled={variantBusy}
                  sx={{ mt: 0.5, textTransform: 'none' }}
                >
                  Add UPC
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
        {isEdit && (
          <Button
            startIcon={<MergeTypeIcon />}
            onClick={() => setMergeOpen(true)}
            sx={{ mr: 'auto', textTransform: 'none' }}
            disabled={createMutation.isPending || updateMutation.isPending}
          >
            Merge another line into this one…
          </Button>
        )}
        <Button
          onClick={() => {
            if (isEdit) {
              setEditOpen(false);
              setEditing(null);
            } else {
              setCreateOpen(false);
            }
            resetForm();
          }}
          sx={{ textTransform: 'none' }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={() => (isEdit ? submitEdit() : submitCreate())}
          disabled={createMutation.isPending || updateMutation.isPending}
          sx={{ textTransform: 'none' }}
        >
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box>
      {formDialog(false)}
      {formDialog(true)}

      <Dialog open={mergeOpen} onClose={() => !mergeSaving && setMergeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Merge catalog line</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            Stock balances, movement history, alternate UPCs, and price or cost audit history from the line you pick
            below are combined into <strong>{editing?.upc}</strong>. That other catalog line is deleted.
          </Typography>
          <Autocomplete<TomVendingInventoryRow>
            options={mergeOptions}
            loading={mergeLoading}
            filterOptions={(x) => x}
            value={mergeTarget}
            onChange={(_, v) => setMergeTarget(v)}
            inputValue={mergeSearch}
            onInputChange={(_, v) => setMergeSearch(v)}
            getOptionLabel={(o) => `${o.upc} — ${o.name?.trim() || '—'}`}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Catalog line to absorb"
                placeholder="Search UPC or name…"
                sx={{ mt: 1 }}
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMergeOpen(false)} disabled={mergeSaving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={!mergeTarget || mergeSaving}
            onClick={() => void confirmMerge()}
          >
            Merge
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Price and cost history
          {historyFor ? (
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ display: 'block', fontWeight: 400, mt: 0.5 }}
            >
              {historyFor.upc}
              {historyFor.name ? ` · ${historyFor.name}` : ''}
            </Typography>
          ) : null}
        </DialogTitle>
        <DialogContent>
          {historyLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={32} />
            </Box>
          ) : historyRows.length === 0 ? (
            <Typography color="text.secondary">No price or cost changes recorded yet.</Typography>
          ) : (
            <List dense>
              {historyRows.map((h) => (
                <ListItem
                  key={h.id}
                  alignItems="flex-start"
                  sx={{ flexDirection: 'column', borderBottom: 1, borderColor: 'divider', py: 1.5 }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {new Date(h.createdAt).toLocaleString()}
                    {h.changedByDisplayName || h.changedByEmail
                      ? ` · ${h.changedByDisplayName || h.changedByEmail}`
                      : ''}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {h.field === 'price' ? 'Price' : 'Cost'}: {formatCents(h.previousValueCents)} →{' '}
                    {formatCents(h.newValueCents)}
                  </Typography>
                  {h.note ? (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                      {h.note}
                    </Typography>
                  ) : null}
                </ListItem>
              ))}
            </List>
          )}
          {!historyLoading && historyTotal > historyRows.length ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              Showing {historyRows.length} of {historyTotal}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={warehouseDialogOpen}
        onClose={() => {
          if (!warehouseSaving) {
            setWarehouseDialogOpen(false);
            setWarehouseRow(null);
          }
        }}
      >
        <DialogTitle>Warehouse stock</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, mt: 0.5 }}>
            {warehouseRow?.upc}
            {warehouseRow?.name ? ` · ${warehouseRow.name}` : ''}
          </Typography>
          <TextField
            label="Quantity at warehouse"
            type="number"
            size="small"
            fullWidth
            slotProps={{ htmlInput: { min: 0 } }}
            value={warehouseQty}
            onChange={(e) => setWarehouseQty(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setWarehouseDialogOpen(false);
              setWarehouseRow(null);
            }}
            disabled={warehouseSaving}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void saveWarehouseStock()} disabled={warehouseSaving}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <DialogTitle>Delete item</DialogTitle>
        <DialogContent>
          <Typography>
            Delete <strong>{deleting?.upc}</strong>
            {deleting?.name ? ` (${deleting.name})` : ''}? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={priceImportOpen} onClose={() => setPriceImportOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Import sale prices</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Paste CSV with UPC and sale price per line (comma, tab, or semicolon separated). Example:{' '}
            <code>012345678905,1.25</code>
          </Typography>
          <TextField
            multiline
            minRows={8}
            fullWidth
            value={priceImportText}
            onChange={(e) => setPriceImportText(e.target.value)}
            placeholder={'012345678905,1.25\n012345678912,2.00'}
          />
          {priceImportResult ? (
            <Typography variant="body2" sx={{ mt: 2, whiteSpace: 'pre-wrap' }}>
              {priceImportResult}
            </Typography>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPriceImportOpen(false)}>Close</Button>
          <Button
            variant="contained"
            disabled={priceImportLoading || !priceImportText.trim()}
            onClick={() => {
              void (async () => {
                const parsed: Array<{ upc: string; priceDollars: number }> = [];
                for (const line of priceImportText.split(/\r?\n/)) {
                  const t = line.trim();
                  if (!t || t.startsWith('#')) continue;
                  const parts = t.split(/[,;\t]/).map((p) => p.trim().replace(/^"|"$/g, ''));
                  if (parts.length < 2) continue;
                  const upc = parts[0];
                  const price = parseFloat(parts[1].replace(/[$"]/g, ''));
                  if (!upc || Number.isNaN(price) || price < 0) continue;
                  parsed.push({ upc, priceDollars: price });
                }
                if (parsed.length === 0) {
                  showError('No valid rows found');
                  return;
                }
                setPriceImportLoading(true);
                setPriceImportResult(null);
                try {
                  const r = await bulkUpsertTomVendingSalePrices({
                    rows: parsed,
                    note: 'CSV bulk price import',
                  });
                  setPriceImportResult(
                    `Updated ${r.updated} item(s). Skipped ${r.skipped} unchanged.` +
                      (r.notFoundUpcs.length > 0
                        ? `\nNot found (${r.notFoundUpcs.length}): ${r.notFoundUpcs.slice(0, 20).join(', ')}${r.notFoundUpcs.length > 20 ? '…' : ''}`
                        : ''),
                  );
                  setTableRefreshKey((k) => k + 1);
                  showSuccess(`Updated ${r.updated} sale price(s)`);
                } catch (e: unknown) {
                  showError(e instanceof Error ? e.message : 'Import failed');
                } finally {
                  setPriceImportLoading(false);
                }
              })();
            }}
          >
            {priceImportLoading ? 'Importing…' : 'Import'}
          </Button>
        </DialogActions>
      </Dialog>

      <TitleBar
        breadcrumbs={breadcrumbs}
        title="Catalog"
        rightContent={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              component={RouterLink}
              to="/app/toms-vending/inventory/quick-edit"
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Quick edit
            </Button>
            <Button
              onClick={() => {
                setPriceImportText('');
                setPriceImportResult(null);
                setPriceImportOpen(true);
              }}
              variant="outlined"
              sx={{ textTransform: 'none' }}
            >
              Import prices
            </Button>
            <Button onClick={openCreate} variant="contained" sx={{ textTransform: 'none' }}>
              + Add item
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: 2 }}>
        <MaterialReactTable
          columns={columns}
          data={data}
          manualPagination
          manualSorting
          enableGlobalFilter={false}
          onPaginationChange={setPagination}
          onSortingChange={setSorting}
          rowCount={totalRows}
          initialState={{
            density: 'compact',
            columnVisibility: { createdAt: false, brandName: false, categoryName: false, schoolApproved: false },
          }}
          state={{
            isLoading,
            pagination,
            sorting,
          }}
          renderTopToolbarCustomActions={() => (
            <TextField
              placeholder="Search UPC, name, brand, category…"
              variant="outlined"
              size="small"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPagination((p) => ({ ...p, pageIndex: 0 }));
              }}
              sx={{ width: 320 }}
            />
          )}
        />
      </Box>
    </Box>
  );
};

export default TomsVendingInventoryPage;
